import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Plus, CircleNotch } from '@phosphor-icons/react';

/**
 * Add / Edit Draw Dialog — extracted from Dashboard.js.
 * Owns the pre-fill of next draw # and next EuroMillions draw date when opening
 * in "Add" mode (Tuesday → Friday +3, Friday → Tuesday +4).
 */
export default function AddDrawDialog({
  dialogOpen,
  setDialogOpen,
  editingDraw,
  draws,
  formData,
  setFormData,
  formLoading,
  handleSubmit,
  resetForm,
}) {
  return (
    <Dialog
      open={dialogOpen}
      onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) resetForm();
        // When the dialog is opened (not edit-mode), pre-fill next draw # and next draw date
        if (open && !editingDraw && draws.length > 0) {
          const latest = draws[0];
          const nextDrawNum = (latest.draw_number || 0) + 1;
          // Compute next EuroMillions draw date (Tuesday or Friday)
          let nextDate = '';
          try {
            const [d, m, y] = (latest.draw_date || '').split('/').map(Number);
            if (d && m && y) {
              const dt = new Date(y, m - 1, d);
              // Tue (2) → next is Fri (+3); Fri (5) → next is Tue (+4); fallback +3
              const dow = dt.getDay();
              const addDays = dow === 2 ? 3 : dow === 5 ? 4 : 3;
              dt.setDate(dt.getDate() + addDays);
              nextDate = `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
            }
          } catch { /* fallback empty */ }
          setFormData((prev) => ({
            ...prev,
            draw_number: prev.draw_number || String(nextDrawNum),
            draw_date: prev.draw_date || nextDate,
          }));
        }
      }}
    >
      <DialogTrigger asChild>
        <Button className="btn-primary flex items-center gap-2" data-testid="add-draw-btn">
          <Plus size={18} />
          Add Draw
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
            {editingDraw ? 'Edit Draw' : 'Add New Draw'}
          </DialogTitle>
          <DialogDescription>
            {editingDraw ? 'Update the draw details below.' : 'Enter the draw details. Main balls: 1-50, Lucky Stars: 1-12.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-medium text-gray-500">
                Draw Number
              </Label>
              <Input
                type="number"
                value={formData.draw_number}
                onChange={(e) => setFormData({ ...formData, draw_number: e.target.value })}
                required
                className="form-input mt-1"
                data-testid="form-draw-number"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-medium text-gray-500">
                Draw Date (DD/MM/YYYY)
              </Label>
              <Input
                type="text"
                placeholder="DD/MM/YYYY"
                value={formData.draw_date}
                onChange={(e) => setFormData({ ...formData, draw_date: e.target.value })}
                required
                className="form-input mt-1"
                data-testid="form-draw-date"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-2 block">
              Main Balls (1-50)
            </Label>
            <div className="grid grid-cols-5 gap-2">
              {[1, 2, 3, 4, 5].map((num) => (
                <Input
                  key={num}
                  type="number"
                  min="1"
                  max="50"
                  placeholder={`B${num}`}
                  value={formData[`ball_${num}`]}
                  onChange={(e) => setFormData({ ...formData, [`ball_${num}`]: e.target.value })}
                  required
                  className="form-input text-center"
                  data-testid={`form-ball-${num}`}
                />
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-2 block">
              Lucky Stars (1-12)
            </Label>
            <div className="grid grid-cols-2 gap-2 max-w-[200px]">
              {[1, 2].map((num) => (
                <Input
                  key={num}
                  type="number"
                  min="1"
                  max="12"
                  placeholder={`LS${num}`}
                  value={formData[`lucky_star_${num}`]}
                  onChange={(e) => setFormData({ ...formData, [`lucky_star_${num}`]: e.target.value })}
                  required
                  className="form-input text-center"
                  data-testid={`form-lucky-star-${num}`}
                />
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1 block">
              Winner Amount (optional — leave empty if no winner)
            </Label>
            <Input
              type="text"
              placeholder="e.g. 181073415.70"
              value={formData.winner_amount || ''}
              onChange={(e) => setFormData({ ...formData, winner_amount: e.target.value })}
              className="form-input"
              data-testid="form-winner-amount"
            />
            <p className="text-[10px] text-gray-400 mt-0.5">Enter jackpot amount in GBP (numbers only, no £ sign). This updates the Winner Data window.</p>
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="flex-1 btn-secondary"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={formLoading}
              className="flex-1 btn-primary flex items-center justify-center gap-2"
              data-testid="form-submit-btn"
            >
              {formLoading ? (
                <>
                  <CircleNotch size={18} className="animate-spin" />
                  Saving...
                </>
              ) : (
                editingDraw ? 'Update Draw' : 'Add Draw'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
